/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2022, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import _ from 'lodash';

import { PlanName } from '../../../ts/plan';

export type CatalogItemDetails = {
  id: string;
  title: string;
  description: string;
  specification: any;
  plan_available_in?: string[];
  waFileName?: string;
};

/*
  List of catalog items that needs to be added to catalog.
  Future extension can be added here.

  id - a unique identifier for the extension that should NOT be changed,
  description - some description of the extension that will be shown in the tile
  plan_available_in - the instance plans that this extension will be available in
*/
const catalogItems: ReadonlyArray<CatalogItemDetails> = Object.freeze([
  {
    id: 'Segment',
    title: 'Segment',
    description:
      "Get a better understanding of your users' end-to-end journeys by combining your assistant's data with other sources.",
    specification: {
      system: 'segment'
    },
    plan_available_in: [
      PlanName.ENTERPRISE,
      PlanName.ENTERPRISE_DATA_ISOLATION,
      PlanName.PLUS
    ]
  }
]);

/*
  This method returns the default catalog items that need to be added to the assistant.
  In order to show a catalog item tile in the UI that can be then added to the assistant
  (or to be given an upgrade message), we need to create the catalog item on this assistant.
  Each item returned will include the id and description as well as which plans will allow you
  to add an instance of the catalog item.
*/
export const getDefaultCatalogItems = (): ReadonlyArray<CatalogItemDetails> => {
  return catalogItems;
};

/*
  This method returns true if the passed plan is one of the available plans for the catalog item.
  plan - current instance plan
  id - default catalog id
  enableOnNonEnterprisePlan - if true, enable the item on non enterprise plan
*/
export const isUpgradeRequired = (
  plan: PlanName,
  id: string,
  enableOnNonEnterprisePlan: boolean
): boolean => {
  const catalogItem = catalogItems.find(
    item =>
      item.id === id &&
      (_.includes(item.plan_available_in, plan) || enableOnNonEnterprisePlan)
  );
  return _.isNil(catalogItem);
};
